import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import useTitle from "@shared/hooks/useTitle";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  FileText,
  Users,
  Settings,
  Activity,
  Shield,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  Package,
  DollarSign,
} from "lucide-react";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import { ADMIN_QUERY_KEYS } from "../constants";
import { adminAnalyticsService } from "../services/analytics-service";

export default function AdminDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useTitle(t("admin.dashboard.title"));

  // Fetch system statistics
  const { data, isLoading } = useQuery({
    queryKey: [ADMIN_QUERY_KEYS.SYSTEM_STATS],
    queryFn: () => adminAnalyticsService.getSystemStats(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const stats = data?.data || {};

  return (
    <div className="space-y-6">
      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t("admin.dashboard.totalUsers")}
          value={stats.totalUsers || 0}
          icon={Users}
          color="bg-blue-500"
          trend="+12%"
        />
        <StatCard
          title={t("admin.dashboard.activePackages")}
          value={stats.activePackages || 0}
          icon={Package}
          color="bg-green-500"
          trend="+8%"
        />
        <StatCard
          title={t("admin.dashboard.monthlyRevenue")}
          value={`${stats.monthlyRevenue || 0} MAD`}
          icon={DollarSign}
          color="bg-purple-500"
          trend="+15%"
        />
        <StatCard
          title={t("admin.dashboard.systemHealth")}
          value={stats.systemHealth || "Good"}
          icon={Activity}
          color="bg-green-500"
          isText
        />
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>{t("admin.dashboard.quickActions")}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <ThreeDotLoader />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <ActionCard
                title={t("admin.dashboard.viewAnalytics")}
                description={t("admin.dashboard.viewAnalyticsDesc")}
                icon={BarChart3}
                onClick={() => navigate("/admin/analytics")}
                badge={stats.pendingReports}
              />
              <ActionCard
                title={t("admin.dashboard.systemLogs")}
                description={t("admin.dashboard.systemLogsDesc")}
                icon={FileText}
                onClick={() => navigate("/admin/logs")}
                badge={stats.recentErrors}
                badgeVariant="destructive"
              />
              <ActionCard
                title={t("admin.dashboard.userManagement")}
                description={t("admin.dashboard.userManagementDesc")}
                icon={Users}
                onClick={() => navigate("/users")}
                badge={stats.pendingUsers}
              />
              <ActionCard
                title={t("admin.dashboard.systemSettings")}
                description={t("admin.dashboard.systemSettingsDesc")}
                icon={Settings}
                onClick={() => navigate("/admin/settings")}
              />
              <ActionCard
                title={t("admin.dashboard.securityCenter")}
                description={t("admin.dashboard.securityCenterDesc")}
                icon={Shield}
                onClick={() => navigate("/admin/logs?level=security")}
                badge={stats.securityAlerts}
                badgeVariant="warning"
              />
              <ActionCard
                title={t("admin.dashboard.performanceMonitor")}
                description={t("admin.dashboard.performanceMonitorDesc")}
                icon={TrendingUp}
                onClick={() => navigate("/admin/analytics?type=performance")}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>{t("admin.dashboard.systemStatus")}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <ThreeDotLoader />
            ) : (
              <div className="space-y-3">
                <StatusItem
                  label={t("admin.dashboard.apiStatus")}
                  status="operational"
                  icon={CheckCircle}
                />
                <StatusItem
                  label={t("admin.dashboard.databaseStatus")}
                  status="operational"
                  icon={CheckCircle}
                />
                <StatusItem
                  label={t("admin.dashboard.emailService")}
                  status="operational"
                  icon={CheckCircle}
                />
                <StatusItem
                  label={t("admin.dashboard.cronJobs")}
                  status="operational"
                  icon={CheckCircle}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("admin.dashboard.recentActivity")}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <ThreeDotLoader />
            ) : (
              <div className="space-y-3">
                <ActivityItem
                  action={t("admin.dashboard.newUserRegistered")}
                  time="2 minutes ago"
                />
                <ActivityItem
                  action={t("admin.dashboard.invoiceGenerated")}
                  time="15 minutes ago"
                />
                <ActivityItem
                  action={t("admin.dashboard.packageDelivered")}
                  time="1 hour ago"
                />
                <ActivityItem
                  action={t("admin.dashboard.systemBackup")}
                  time="2 hours ago"
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pending Items */}
      <Card>
        <CardHeader>
          <CardTitle>{t("admin.dashboard.pendingItems")}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <ThreeDotLoader />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <PendingCard
                title={t("admin.dashboard.pendingApprovals")}
                count={stats.pendingApprovals || 0}
                onClick={() => navigate("/banking/pending-requests")}
              />
              <PendingCard
                title={t("admin.dashboard.openTickets")}
                count={stats.openTickets || 0}
                onClick={() => navigate("/tickets")}
              />
              <PendingCard
                title={t("admin.dashboard.pendingInvoices")}
                count={stats.pendingInvoices || 0}
                onClick={() => navigate("/invoices?status=pending")}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Helper Components
function StatCard({ title, value, icon: Icon, color, trend, isText = false }) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p
              className={`text-2xl font-bold mt-2 ${
                isText ? "capitalize" : ""
              }`}
            >
              {value}
            </p>
            {trend && !isText && (
              <p className="text-sm text-green-500 mt-1">{trend}</p>
            )}
          </div>
          <div
            className={`h-12 w-12 rounded-full ${color} bg-opacity-10 flex items-center justify-center`}
          >
            <Icon className={`h-6 w-6 ${color.replace("bg-", "text-")}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ActionCard({
  title,
  description,
  icon: Icon,
  onClick,
  badge,
  badgeVariant = "default",
}) {
  return (
    <Card
      className="cursor-pointer hover:shadow-lg transition-shadow"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-sm">{title}</h3>
              {badge > 0 && (
                <Badge variant={badgeVariant} className="ml-2">
                  {badge}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusItem({ label, status, icon: Icon }) {
  const isOperational = status === "operational";

  return (
    <div className="flex items-center justify-between">
      <span className="text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <Icon
          className={`h-4 w-4 ${
            isOperational ? "text-green-500" : "text-red-500"
          }`}
        />
        <span
          className={`text-sm ${
            isOperational ? "text-green-500" : "text-red-500"
          }`}
        >
          {isOperational ? "Operational" : "Down"}
        </span>
      </div>
    </div>
  );
}

function ActivityItem({ action, time }) {
  return (
    <div className="flex items-center gap-3">
      <div className="p-1 rounded-full bg-muted">
        <Clock className="h-3 w-3 text-muted-foreground" />
      </div>
      <div className="flex-1">
        <p className="text-sm">{action}</p>
        <p className="text-xs text-muted-foreground">{time}</p>
      </div>
    </div>
  );
}

function PendingCard({ title, count, onClick }) {
  return (
    <div
      className="border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold mt-2">{count}</p>
        </div>
        <AlertCircle
          className={`h-8 w-8 ${
            count > 0 ? "text-yellow-500" : "text-muted-foreground"
          }`}
        />
      </div>
    </div>
  );
}
